import { createContext, useContext, useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import localforage from "localforage";
import { useAppServices } from "../hook/services";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import env from "../config/env";

const UserContext = createContext();
UserContext.displayName = "UserContext";

function UserProvider({ children }) {
  const searchParams = new URLSearchParams(document.location.search);
  const isSupport = searchParams.get("isSupport");
  const navigate = useNavigate();

  const BASE_URL = `${env.API_URL}/v1`;
  const FACEBOOK_AUTH = env.FACEBOOK_AUTH;
  const pathname = window.location.pathname;

  // Handle app-integrations route differently
  let encryptedData, decryptedData, userEmail, userLocationId;

  if (pathname.startsWith("/app-integrations/")) {
    // For app integration routes, get location ID from URL params
    const urlParams = new URLSearchParams(window.location.search);
    userLocationId = urlParams.get("state"); // state parameter contains location ID
    userEmail = null; // Not available in this context
  } else {
    // Original logic for other routes
    encryptedData = pathname.split("/")[1];
    if (encryptedData) {
      try {
        decryptedData = atob(encryptedData);
        userEmail = decryptedData.split(":")[0];
        userLocationId = decryptedData.split(":")[1];
      } catch (error) {
        console.error("Failed to decode user data from URL:", error);
        userEmail = null;
        userLocationId = null;
      }
    }
  }

  const Service = useAppServices();
  const [user, setUser] = useState({});
  const [path, setPath] = useState(window.location.pathname + `${window.location.search}`);
  const [userActiveLocation, setUserActiveLocation] = useState();
  const [ActiveApps, setActiveApps] = useState();
  const [userLocations, setUserLocations] = useState([]);
  const [checkIfGhlUser, setCheckIfGhlUser] = useState(false);
  const [agencyData, setAgencyData] = useState();
  const [isConnected, setIsConnected] = useState(false);
  const [GHLSessionKey, setGHLSessionKey] = useState("");
  const [accountActivePlan, setAccountActivePlan] = useState("");
  const [agentShow, setagentshow] = useState(false);
  const [accountPlugins, setaccountPlugins] = useState([]);
  const service = useAppServices();
  const [isFreePlan, setIsFreePlan] = useState(false);
  const location = useLocation()

  const contextuploadFilefrominput = async (
    files,
    profileId,
    userName,
    assistant_ids,
    compType
  ) => {
    const audioformData = new FormData();
    const formData = new FormData();
    const audiofilestype = [
      "audio/mpeg", // mp3
      "video/mp4", // mp4
      "video/mpeg", // mpeg
      "audio/mp4", // m4a
      "audio/wav", // wav
    ];
    const audiofiles = [];
    const otherFiles = [];
    const assistants = assistant_ids?.map((item) => item.value);

    for (let index = 0; index < files.length; index++) {
      const fileType = files[index].file
        ? files[index].file.type
        : files[index].type;

      if (audiofilestype.includes(fileType)) {
        audiofiles.push(files[index]);
      } else {
        otherFiles.push(files[index]);
      }
    }

    if (compType === "shared_knowledge") {
      const sharedForm = new FormData();
      for (const f of [...audiofiles, ...otherFiles]) {
        sharedForm.append("file", f.file ? f.file : f);
      }
      sharedForm.append("username", userName || "");
      try {
        const response = await axios.post(
          `${BASE_URL}/chat/profiles/${profileId}/files/upload`,
          sharedForm,
          { "Content-Type": "multipart/form-data" }
        );
        return response;
      } catch (error) {
        console.log(error);
        return error?.response || { data: { success: false } };
      }
    }

    // Append allowed files to formData
    for (let file of audiofiles) {
      audioformData.append("file", file.file ? file.file : file);
      audioformData.append("translate", file.isChecked);
    }
    for (let file of otherFiles) {
      formData.append("file", file.file ? file.file : file);
    }
    audioformData.append("profile_id", profileId);
    audioformData.append("username", userName);
    audioformData.append("model_name", "openai/gpt-4o");
    formData.append("profile_id", profileId);
    formData.append("username", userName);
    formData.append("assistant_ids", JSON.stringify(assistants));
    formData.append("model_name", "openai/gpt-4o");

    // You can now handle `otherFiles` array separately if needed
    if (audiofiles.length > 0) {
      try {
        const response = await axios.post(
          `${BASE_URL}/chat/audio`,
          audioformData,
          {
            "Content-Type": "multipart/form-data", // Optional: axios sets this automatically
          }
        );
        if (response) {
          return response;
        }
      } catch (error) {
        console.log(error);
      }
    }
    if (otherFiles.length > 0) {
      try {
        const response = await axios.post(
          compType === "data_interpreter"
            ? `${BASE_URL}/chat/upload-data-interpreter`
            : `${BASE_URL}/chat/assistants/files/upload`,
          formData,
          {
            "Content-Type": "multipart/form-data", // Optional: axios sets this automatically
          }
        );
        if (response) {
          return response;
        }
      } catch (error) {
        console.log(error);
      }
    }
  };


  const contextUploadFilesAsync = async (
    files,
    profileId,
    userName,
    assistant_ids,
    userId,
    compType,
  ) => {
    const formData = new FormData();
    const assistants = assistant_ids?.map((item) => item.value);
    for (const f of files) {
      formData.append("file", f.file ? f.file : f);
    }
    formData.append("profile_id", profileId);
    formData.append("username", userName || "");
    formData.append("user_id", userId || user?._id || "");
    formData.append("assistant_ids", JSON.stringify(assistants || []));
    formData.append("model_name", "openai/gpt-4o");
    formData.append("compType", compType || "knowledge_base");
    formData.append("isStreaming", true);

    try {
      const response = await axios.post(
        `${BASE_URL}/chat/assistants/files/upload-async`,
        formData,
        { "Content-Type": "multipart/form-data" },
      );
      return response?.data; 
    } catch (error) {
      return error?.response?.data || { success: false };
    }
  };

  const Update = async (updates) => {
    const newUser = { ...user, ...updates };
    setUser(newUser);
    await localforage.setItem("user", newUser);
  };

  const clear = async () => {
    try {
      await localforage.clear();
    } catch (err) {
      console.error("Error clearing localforage on logout", err);
    }
    setUser({});
    setUserActiveLocation({});
    setPath('');

    window.location.replace("/auth/sign-in");
  };

  const saveActiveLocation = async (activeLocation) => {
    await localforage.setItem("active_location", activeLocation);
    setUserActiveLocation(activeLocation);
  };

  const saveActivePlans = async (activeplans) => {
    await localforage.setItem("active_plans", activeplans);
    setActiveApps(activeplans);
  };

  const clearAllData = async () => {
    const sessionKeyValue = await localforage.getItem("sessionKey");
    // alert(sessionKeyValue)
    await localforage
      .clear()
      .then(() => {
        localforage.setItem("sessionKey", sessionKeyValue);
      })
      .catch((err) => {
        console.error("Error clearing localforage data", err);
      });
  };

  const isValidLocationId = (locationId) => {
    const locationIdRegex = /^[A-Za-z0-9]{20}$/;
    return locationIdRegex.test(locationId);
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const checkLocation = async (
    userLocationId,
    userEmail,
    userName,
    redirectType = "GHL"
  ) => {
    const payload = { location_id: userLocationId };
    try {
      const response = await axios.post(
        `${BASE_URL}/chat/assistants/locationCheck`,
        payload
      );

      if (response?.data?.data?.data.length > 0) {
        setIsConnected(true);
        check_if_account_have_permissionto_create_agent();
      } else {
        // setTimeout(() => {
        //   window.location.href = `/onboarding?profile_id=${userLocationId}&profile_name=${userName}&username=${userName}&type=${redirectType}&email=${userEmail}`;
        //   check_if_account_have_permissionto_create_agent();
        // }, [2000])
      }
    } catch (error) {
      // setTimeout(() => {
      //   window.location.href = `/onboarding?profile_id=${userLocationId}&profile_name=${userName}&username=${userName}&type=${redirectType}&email=${userEmail}`;
      //   check_if_account_have_permissionto_create_agent();
      // }, [4000])
    }
  };

  useEffect(() => {
    window.addEventListener('loadstart', () => {
      setPath(location.pathname + location.search)
    })
  }, [location])

  useEffect(() => {
    if (encryptedData !== 'auth' && encryptedData !== 'app') {
      refreshpage()
    }
  }, [encryptedData, decryptedData, userEmail])

  const value = useMemo(
    () => [
      user,
      Update,
      clear,
      setUserActiveLocation,
      userActiveLocation,
      saveActiveLocation,
      isValidEmail,
      isValidLocationId,
      userLocations,
      checkIfGhlUser,
      setUserLocations,
      agencyData,
      ActiveApps,
      isConnected,
      GHLSessionKey,
      checkLocation,
      contextuploadFilefrominput,
      accountActivePlan,
      agentShow,
      accountPlugins,
      path,
      contextUploadFilesAsync,
    ],
    [
      user,
      Update,
      clear,
      setUserActiveLocation,
      userActiveLocation,
      saveActiveLocation,
      isValidEmail,
      isValidLocationId,
      userLocations,
      checkIfGhlUser,
      setUserLocations,
      agencyData,
      ActiveApps,
      isConnected,
      GHLSessionKey,
      checkLocation,
      contextuploadFilefrominput,
      accountActivePlan,
      agentShow,
      accountPlugins,
      path,
      contextUploadFilesAsync
    ]
  );

  const getUser = async (localUser, activeLocation) => {
    const token = await localforage.getItem("token");
    if (!activeLocation?.id) {
      return
    }
    const { response } = await Service.user.getchatuser({
      query: `_id=${localUser?._id}&location_id=${activeLocation?.id}`,
      token,
    });
    if (response) {
      const updatedUser = { ...response.data, token };
      setUser({ ...localUser, ...updatedUser });
      await localforage.setItem("user", { ...localUser, ...updatedUser });
      // setUserActiveLocation(response.activeLocations[0]);
      setUserLocations(response.activeLocations);

      window.maybe_v2_usr = {
        usr_id: updatedUser?.userId,
        usr_name: updatedUser?.userName,
        usr_email: updatedUser?.email,
      };
      if (response.activeLocations[0]?.type !== "free") {
        getAppsList(response.activeLocations[0].plan_id);
      }
    }
  };

  const updateUser = async () => {
    const localUser = await localforage.getItem("user");
    const activeLocation = await localforage.getItem("active_location");
    const agencyInfo = await localforage.getItem("agency");
    const token = await localforage.getItem("token");
    setUser({ ...localUser, ...(token ? { token } : {}) });
    setUserActiveLocation(activeLocation);
    setAgencyData(agencyInfo);
    getUser(localUser, activeLocation)
  };

  const refreshpage = async () => {
    const localUser = await localforage.getItem("user");
    if (!localUser) {
      const payload = {
        email: userEmail,
        location_id: userLocationId,
        ghlUser: true,
        role: "agency",
      };
      const { response } = await Service.auth.login({ payload });
      if (response) {
        const { token, ...userData } = response.data;
        await localforage.setItem("token", token);
        await localforage.setItem("user", { ...userData });
        await localforage.setItem("agency", response.agency);
        await localforage.setItem('active_location', response?.activeLocations[0])
        updateUser();
      }
    }
  };

  const sessionstore = async () => {
    try {
      const currentUrl = window.location.href;
      const url = new URL(currentUrl);
      const sessionKey = url.searchParams.get("sessionKey");

      if (sessionKey) {
        await localforage.setItem("sessionKey", sessionKey);
        setGHLSessionKey(sessionKey);
      } else {
        const storedSessionKey = await localforage.getItem("sessionKey");
        if (storedSessionKey) {
          setGHLSessionKey(storedSessionKey);
        }
      }
    } catch (error) {
      console.error("Failed to store session key:", error);
    }
  };

  // const checkSSOUser = async () => {
  //   const localUser = await localforage.getItem('user')
  //   // clearAllData();
  //   const payload = {
  //     email: userEmail,
  //     location_id: userLocationId,
  //     ghlUser: true,
  //     role: "agency",
  //   };
  //   const { response } = await Service.auth.login({ payload });
  //   if (response) {
  //     const { token, ...userData } = response.data;
  //     await localforage.setItem("token", token);
  //     await localforage.setItem("user", { ...localUser, ...userData });
  //     if (response.brand) await localforage.setItem("brand", response.brand);
  //     if (response.agency) {
  //       await localforage.setItem("agency", response.agency);
  //       setAgencyData(response.agency);
  //     }
  //     updateUser(response.data);
  //     saveActiveLocation(response.activeLocations[0]);
  //     setUserLocations(response.activeLocations);
  //     sessionstore();
  //     const redirectUrl = `/${btoa(
  //       `${userEmail}:${userLocationId}`
  //     )}/app/chatapp${isSupport === "true" ? "?isSupport=true" : ""}`;
  //     navigate(redirectUrl);
  //   } else {
  //     const redirectUrl = `/auth/sign-in`;
  //     navigate(redirectUrl);
  //   }
  // };

  const getAppsList = async (plan_id) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/accounts/getAppsList?plan_id=${plan_id}`
      );
      if (
        response &&
        response.data &&
        response.data.data &&
        response.data.data[0]
      ) {
        const apps = response.data.data[0].apps;
        const uniqueApps = apps.filter(
          (item, index) => apps.indexOf(item) === index
        );
        response.data.data[0].apps = uniqueApps;
        saveActivePlans(response.data.data[0]);
        setAccountActivePlan(response.data.data[0]);
      }
    } catch (error) {
      console.error("Error fetching apps list:", error);
    }
  };

  const check_if_account_have_permissionto_create_agent = async () => {
    const activeLocation = await localforage.getItem("active_location");

    const response = await axios.get(
      `${BASE_URL}/accounts/getbyid?id=${activeLocation?.id}`
    );
    if (response.data.data?.agenct_config?.allow) {
      setagentshow(true);
      setaccountPlugins(response.data.data?.agenct_config?.plugins);
    } else {
      setagentshow(false);
      setaccountPlugins([]);
    }
  };
  useEffect(() => {
    updateUser()
  }, []);


  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}

function useUserInfo() {
  return useContext(UserContext) || [];
}

UserProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { UserProvider, useUserInfo };
